import { useSearchParams as useURLSearchParams } from 'react-router-dom'

/**
 * A convenient wrapper for interacting with search parameters in the browser's URL.
 * This hook provides an easy way to read and modify a specific search parameter
 * identified by the given paramKey.
 *
 * @param {string} paramKey - The key of the search parameter in the URL to interact with.
 * @returns {[string, (value: string ) => void]} - Returns the current
 *   value of the specified search parameter and a function to set its value or remove it.
 */
export const useSearchParam = (
  paramKey: string,
): [string, (value: string) => void] => {
  const [searchParams, setSearchParams] = useURLSearchParams()
  const paramValue = searchParams.get(paramKey) || ''

  const setParamValue = (value: string) => {
    if (value === '') {
      deleteParamValue()
    } else {
      setSearchParams(
        prev => {
          prev.set(paramKey, value)
          return prev
        },
        { replace: true },
      )
    }
  }

  const deleteParamValue = () => {
    setSearchParams(
      prev => {
        prev.delete(paramKey)
        return prev
      },
      { replace: true },
    )
  }

  return [paramValue, setParamValue]
}
